import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {  RegionFormData } from '../../../models/region.model';
import { UserFormData } from '../../../models/user.model';
import { ValidationService } from '../../../services/validation.service';
import { IdGeneratorService } from '../../../services/shared/id-generator/id-generator.service';
import { CascadeConfirmationComponent } from '../../dialogs/cascade-confirmation/cascade-confirmation.component';
import { faMapMarkerAlt, faPlus, faTrash, faUser, faUserPlus, faChevronDown, faChevronUp, faTimes, faEdit } from '@fortawesome/free-solid-svg-icons';

export interface RegionAdminAssignment {
  regionId: number | string | null;
  adminId: number | null;
  regionName: string;
  adminName: string;
}

export interface RegionsManagerChangeEvent {
  regions: RegionFormData[];
  adminAssignments: RegionAdminAssignment[];
  isValid: boolean;
  errors: { [key: string]: string };
}

export interface AssignExistingAdminEvent {
  adminId: number;
  regionId: number | string | null;
  regionName: string;
}

export interface RemoveAdminFromRegionEvent {
  adminId: number;
  regionId: number | string | null;
  regionName: string;
}

export interface CreateAdminForRegionEvent {
  regionId: number | string | null;
  regionName: string;
  adminData: {
    username: string;
    fullName: string;
    email: string;
    mobile?: string;
    password: string;
  };
}

export interface RegionCascadeAnalysis {
  regionId: number | string | null;
  regionName: string;
  cascadeItems: {
    sites: any[]; // SiteFormData[] - using any to avoid circular imports
    admins: UserFormData[];
    operators: UserFormData[];
  };
  totalItemsToDelete: number;
}

export interface RegionRemovalConfirmedEvent {
  regionIndex: number;
  cascadeAnalysis: RegionCascadeAnalysis;
}

export interface EditAdminEvent {
  adminId: number | string;
  adminData: {
    username: string;
    fullName: string;
    email: string;
    mobile?: string;
    emp_code?: string;
  };
}

export interface DeleteAdminEvent {
  adminId: number | string;
  adminName: string;
}

@Component({
  selector: 'app-regions-manager',
  templateUrl: './regions-manager.component.html',
  styleUrls: ['./regions-manager.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RegionsManagerComponent),
      multi: true
    }
  ]
})
export class RegionsManagerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() editMode: boolean = false;
  @Input() regionsData: RegionFormData[] = [];
  @Input() adminsData: UserFormData[] = [];
  @Input() showValidationErrors: boolean = false;
  @Input() disabled: boolean = false;
  @Input() sitesData: any[] = []; // SiteFormData[] - using any to avoid circular imports
  @Input() operatorsData: UserFormData[] = [];

  @Output() regionsChange = new EventEmitter<RegionsManagerChangeEvent>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() assignExistingAdmin = new EventEmitter<AssignExistingAdminEvent>();
  @Output() removeAdminFromRegion = new EventEmitter<RemoveAdminFromRegionEvent>();
  @Output() createAdminForRegion = new EventEmitter<CreateAdminForRegionEvent>();
  @Output() regionRemovalConfirmed = new EventEmitter<RegionRemovalConfirmedEvent>();
  @Output() editAdminConfirmed = new EventEmitter<EditAdminEvent>();
  @Output() deleteAdminConfirmed = new EventEmitter<DeleteAdminEvent>();

  // Icons
  faMapMarkerAlt = faMapMarkerAlt;
  faPlus = faPlus;
  faTrash = faTrash;
  faUser = faUser;
  faUserPlus = faUserPlus;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faTimes = faTimes;
  faEdit = faEdit;

  regionsForm: FormGroup;
  newAdminForm: FormGroup;
  editAdminForm: FormGroup;
  
  // UI State for inline operations
  showAssignmentDropdown: { [regionIndex: number]: boolean } = {};
  showCreateAdminModal: boolean = false;
  showEditAdminModal: boolean = false;
  currentRegionForAdmin: { id: number | string | null; name: string; index: number } | null = null;
  currentAdminForEdit: UserFormData | null = null;
  
  private destroy$ = new Subject<void>();
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private fb: FormBuilder,
    private idGeneratorService: IdGeneratorService,
    private dialog: MatDialog
  ) {
    this.createForm();
    this.createNewAdminForm();
    this.createEditAdminForm();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscription();
    
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.admin-assignment-dropdown')) {
      this.closeAllDropdowns();
    }
  }

  private createForm(): void {
    this.regionsForm = this.fb.group({
      regions: this.fb.array([])
    });
  }

  private createNewAdminForm(): void {
    this.newAdminForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      mobile: ['', [Validators.pattern(/^[0-9+\-\s()]+$/)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  private createEditAdminForm(): void {
    this.editAdminForm = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      mobile: ['', [Validators.pattern(/^[0-9+\-\s()]+$/)]],
      emp_code: ['']
    });
  }

  private initializeForm(): void {
    const regionsArray = this.regionsForm.get('regions') as FormArray;
    
    while (regionsArray.length !== 0) {
      regionsArray.removeAt(0);
    }

    if (this.regionsData && this.regionsData.length > 0) {
      this.regionsData.forEach(region => {
        regionsArray.push(this.createRegionFormGroup(region));
      });
    } else if (!this.editMode) {
      regionsArray.push(this.createRegionFormGroup());
    }

    if (this.disabled) {
      this.regionsForm.disable();
    } else {
      this.regionsForm.enable();
    }
  }

  private createRegionFormGroup(regionData?: RegionFormData): FormGroup {
    const formGroup = this.fb.group({
      id: [regionData && regionData.id || this.idGeneratorService.generateAlphanumericId()], 
      name: [
        regionData && regionData.name || '',
        [Validators.required, Validators.minLength(2), Validators.maxLength(100)]
      ],
      isNew: [regionData && regionData.isNew !== undefined ? regionData.isNew : true]
    });
    
    return formGroup;
  }

  private setupFormSubscription(): void {
    this.regionsForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitChanges();
      });

    this.regionsForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validationChange.emit(this.regionsForm.valid);
      });
  }

  private emitChanges(): void {
    const formValue = this.regionsForm.value;
    const regions: RegionFormData[] = formValue.regions || [];
    const adminAssignments = this.getAdminAssignments();
    const isValid = this.regionsForm.valid;
    const errors = this.getFormErrors();

    const changeEvent: RegionsManagerChangeEvent = {
      regions,
      adminAssignments,
      isValid,
      errors
    };

    this.regionsChange.emit(changeEvent);
    this.onChange(changeEvent);
  }

  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      this.regionsData = value;
      this.initializeForm();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.regionsForm.disable();
    } else {
      this.regionsForm.enable();
    }
  }

  get regionsArray(): FormArray {
    return this.regionsForm.get('regions') as FormArray;
  }

  addRegion(): void {
    const newRegionGroup = this.createRegionFormGroup();
    this.regionsArray.push(newRegionGroup);
    
    setTimeout(() => {
      const newIndex = this.regionsArray.length - 1;
      const nameInput = document.getElementById(`region_name_${newIndex}`);
      if (nameInput) {
        nameInput.focus();
      }
    }, 100);
  }

  removeRegion(index: number): void {
    if (!this.canRemoveRegion() || this.disabled) return;
    
    const regionId = this.getRegionId(index);
    const regionName = this.getRegionName(index);
    
    // Analyze cascade effect
    const cascadeAnalysis = this.analyzeCascadeEffect(regionId, regionName);
    
    // Show confirmation with cascade details
    this.showCascadeConfirmation(index, cascadeAnalysis);
  }

  private analyzeCascadeEffect(regionId: number | string | null, regionName: string): RegionCascadeAnalysis {
    const dependentSites = this.getSitesInRegion(regionId);
    const dependentAdmins = this.getAdminsInRegion(regionId);
    const affectedOperators = this.getOperatorsInSites(dependentSites);
    
    return {
      regionId,
      regionName,
      cascadeItems: {
        sites: dependentSites,
        admins: dependentAdmins,
        operators: affectedOperators
      },
      totalItemsToDelete: dependentSites.length + dependentAdmins.length + affectedOperators.length
    };
  }

  private getSitesInRegion(regionId: number | string | null): any[] {
    return this.sitesData.filter(site => 
      String(site.region_id) === String(regionId)
    );
  }

  private getAdminsInRegion(regionId: number | string | null): UserFormData[] {
    // Get admins who are ONLY assigned to this region (will be deleted)
    return this.adminsData.filter(admin => 
      admin.regionIds.length === 1 && 
      String(admin.regionIds[0]) === String(regionId)
    );
  }

  private getOperatorsInSites(sites: any[]): UserFormData[] {
    const siteIds = sites.map(site => String(site.id));
    
    // Get operators who are ONLY assigned to sites in this region (will be deleted)
    return this.operatorsData.filter(operator => {
      const operatorSiteIds = operator.siteIds.map(id => String(id));
      const sitesInOtherRegions = operatorSiteIds.filter(siteId => 
        !siteIds.includes(siteId)
      );
      return sitesInOtherRegions.length === 0; // Only in this region's sites
    });
  }

  private showCascadeConfirmation(regionIndex: number, cascadeAnalysis: RegionCascadeAnalysis): void {
    const dialogRef = this.dialog.open(CascadeConfirmationComponent, {
      width: '600px',
      disableClose: true,
      data: {
        title: `Remove Region: ${cascadeAnalysis.regionName}`,
        cascadeAnalysis: cascadeAnalysis
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.executeCascadeRemoval(regionIndex, cascadeAnalysis);
      }
    });
  }

  private executeCascadeRemoval(regionIndex: number, cascadeAnalysis: RegionCascadeAnalysis): void {
    // Emit event to parent to handle the cascade deletion
    this.regionRemovalConfirmed.emit({
      regionIndex,
      cascadeAnalysis
    });
    
    // Remove from form array
    this.regionsArray.removeAt(regionIndex);
    this.emitChanges();
  }

  getAdminsForRegion(regionId: number | string | null): UserFormData[] {
    return this.adminsData.filter(admin => 
      admin.userType === 'admin' && 
      admin.regionIds && 
      admin.regionIds.some(id => String(id) === String(regionId))
    );
  }

  getUnassignedAdmins(): UserFormData[] {
    return this.adminsData.filter(admin => 
      admin.userType === 'admin' && 
      (!admin.regionIds || admin.regionIds.length === 0)
    );
  }

  private getAdminAssignments(): RegionAdminAssignment[] {
    const assignments: RegionAdminAssignment[] = [];
    const regions = this.regionsForm.value.regions || [];

    regions.forEach((region: any) => {
      const assignedAdmins = this.getAdminsForRegion(region.id);
      
      if (assignedAdmins.length === 0) {
        assignments.push({
          regionId: region.id,
          adminId: null,
          regionName: region.name,
          adminName: ''
        });
      } else {
        assignedAdmins.forEach(admin => {
          assignments.push({
            regionId: region.id,
            adminId: admin.id || null,
            regionName: region.name,
            adminName: admin.username || ''
          });
        });
      }
    });

    return assignments;
  }


  getAvailableAdminsForRegion(regionId: number | string | null): UserFormData[] {
    return this.adminsData.filter(admin => 
      admin.userType === 'admin' && 
      (!admin.regionIds || admin.regionIds.length === 0)
    );
  }

  getRegionNamesForAdmin(admin: UserFormData): string {
    return admin.regionNames ? admin.regionNames.join(', ') : '';
  }

  handleAssignAdminClick(regionIndex: number): void {
    const regionId = this.getRegionId(regionIndex);
    const availableAdmins = this.getAvailableAdminsForRegion(regionId);
    
    // If no admins available, open create admin modal directly
    if (availableAdmins.length === 0) {
      this.openCreateAdminModal(regionIndex);
    } else {
      // Show dropdown with available admins
      this.toggleAssignmentDropdown(regionIndex);
    }
  }

  toggleAssignmentDropdown(regionIndex: number): void {
    Object.keys(this.showAssignmentDropdown).forEach(key => {
      if (parseInt(key) !== regionIndex) {
        this.showAssignmentDropdown[parseInt(key)] = false;
      }
    });
    
    this.showAssignmentDropdown[regionIndex] = !this.showAssignmentDropdown[regionIndex];
  }

  closeAllDropdowns(): void {
    this.showAssignmentDropdown = {};
  }

  assignExistingAdminToRegion(adminId: number, regionIndex: number): void {
    const regionId = this.getRegionId(regionIndex);
    const regionName = this.getRegionName(regionIndex);
    
    this.assignExistingAdmin.emit({
      adminId,
      regionId,
      regionName
    });
    
    this.closeAllDropdowns();
  }

  removeAdminFromThisRegion(adminId: number, regionIndex: number): void {
    const regionId = this.getRegionId(regionIndex);
    const regionName = this.getRegionName(regionIndex);
    
    this.removeAdminFromRegion.emit({
      adminId,
      regionId,
      regionName
    });
  }

  openCreateAdminModal(regionIndex: number): void {
    const regionId = this.getRegionId(regionIndex);
    const regionName = this.getRegionName(regionIndex);
    
    this.currentRegionForAdmin = {
      id: regionId,
      name: regionName,
      index: regionIndex
    };
    
    this.showCreateAdminModal = true;
    this.closeAllDropdowns();
    this.newAdminForm.reset();
  }

  closeCreateAdminModal(): void {
    this.showCreateAdminModal = false;
    this.currentRegionForAdmin = null;
    this.newAdminForm.reset();
  }

  openEditAdminModal(admin: UserFormData): void {
    this.currentAdminForEdit = admin;
    const adminData = {
      fullName: admin.userDetails && admin.userDetails.name ? admin.userDetails.name : '',
      username: admin.username || '',
      email: admin.userDetails && admin.userDetails.email ? admin.userDetails.email : '',
      password: admin.password || '',
      mobile: admin.userDetails && admin.userDetails.mobile ? admin.userDetails.mobile : '',
    };
    this.editAdminForm.patchValue(adminData);
    this.showEditAdminModal = true;
  }

  closeEditAdminModal(): void {
    this.showEditAdminModal = false;
    this.currentAdminForEdit = null;
    this.editAdminForm.reset();
  }

  createAdminForCurrentRegion(): void {
    if (this.newAdminForm.valid && this.currentRegionForAdmin) {
      const formValue = this.newAdminForm.value;
      
      this.createAdminForRegion.emit({
        regionId: this.currentRegionForAdmin.id,
        regionName: this.currentRegionForAdmin.name,
        adminData: {
          username: formValue.username,
          fullName: formValue.fullName,
          email: formValue.email,
          password: formValue.password,
          mobile: formValue.mobile,
        }
      });
      
      this.closeCreateAdminModal();
    }
  }

  updateCurrentAdmin(): void {
    if (this.editAdminForm.valid && this.currentAdminForEdit) {
      const formValue = this.editAdminForm.value;
      
      this.editAdminConfirmed.emit({
        adminId: this.currentAdminForEdit.id,
        adminData: {
          username: formValue.username,
          fullName: formValue.fullName,
          email: formValue.email,
          mobile: formValue.mobile,
          emp_code: formValue.emp_code
        }
      });
      
      this.closeEditAdminModal();
    }
  }

  deleteAdmin(admin: UserFormData): void {
    // Check if admin is unassigned (can only delete unassigned admins)
    const isUnassigned = !admin.regionIds || admin.regionIds.length === 0;
    
    if (!isUnassigned) {
      alert('Cannot delete admin that is assigned to regions. Please remove from all regions first.');
      return;
    }
    
    const adminName = admin.userDetails && admin.userDetails.name ? admin.userDetails.name : admin.username;
    const message = 'Are you sure you want to permanently delete admin "' + adminName + '"?\n\nThis action cannot be undone.';
    const confirmed = confirm(message);
    
    if (confirmed) {
      const deleteEvent = {
        adminId: admin.id,
        adminName: adminName
      };
      this.deleteAdminConfirmed.emit(deleteEvent);
    }
  }

  shouldShowError(index: number, fieldName: string): boolean {
    if (!this.showValidationErrors) return false;
    
    const regionGroup = this.regionsArray.at(index) as FormGroup;
    if (!regionGroup) return false;

    const field = regionGroup.get(fieldName);
    if (!field) return false;

    return field.invalid && (field.dirty || field.touched);
  }

  getErrorMessage(index: number, fieldName: string): string {
    const regionGroup = this.regionsArray.at(index) as FormGroup;
    if (!regionGroup) return '';

    const field = regionGroup.get(fieldName);
    if (!field || !field.errors) return '';

    const errors = field.errors;
    
    if (errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }

    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Region Name'
    };
    return labels[fieldName] || fieldName;
  }

  private getFormErrors(): { [key: string]: string } {
    const errors: { [key: string]: string } = {};
    
    this.regionsArray.controls.forEach((regionControl, index) => {
      const regionGroup = regionControl as FormGroup;
      Object.keys(regionGroup.controls).forEach(key => {
        const control = regionGroup.get(key);
        if (control && control.errors && (control.dirty || control.touched)) {
          errors[`region_${index}_${key}`] = this.getErrorMessage(index, key);
        }
      });
    });

    return errors;
  }

  markAllAsTouched(): void {
    this.regionsForm.markAllAsTouched();
  }

  resetForm(): void {
    this.regionsForm.reset();
    this.initializeForm();
  }

  isValid(): boolean {
    return this.regionsForm.valid;
  }

  getValue(): RegionsManagerChangeEvent {
    const formValue = this.regionsForm.value;
    return {
      regions: formValue.regions || [],
      adminAssignments: this.getAdminAssignments(),
      isValid: this.regionsForm.valid,
      errors: this.getFormErrors()
    };
  }

  onBlur(): void {
    this.onTouched();
  }

  canRemoveRegion(): boolean {
    return this.regionsArray.length > 1;
  }

  getRegionId(index: number): number | string | null {
    const regionGroup = this.regionsArray.at(index) as FormGroup;
    const idControl = regionGroup && regionGroup.get('id');
    return idControl ? idControl.value : null;
  }

  getRegionName(index: number): string {
    const regionGroup = this.regionsArray.at(index) as FormGroup;
    const nameControl = regionGroup && regionGroup.get('name');
    return nameControl ? nameControl.value : '';
  }

  getAdminDisplayName(admin: UserFormData | null): string {
    if (!admin) return '';
    return admin.userDetails && admin.userDetails.name ? admin.userDetails.name : admin.username;
  }

  isEditFieldInvalid(fieldName: string): boolean {
    const field = this.editAdminForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  mobileFieldError(fieldName: string): boolean {
    const field = this.newAdminForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

}